import React from "react";
import {
  DatePicker,
  Flex,
  Modal,
  List,
  Typography,
  Button,
  Form,
  Input,
  Popconfirm,
  Select,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { format, isBefore, startOfToday } from "date-fns";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../../utils/consts";
import axios from "axios";

const { Text } = Typography;

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  mode?: "create" | "edit";
  modalData?: any;
  selectedMonth: any;
  userId: any;
  onUpdate: () => void;
  userWorkLocations?: any[];
  defaultLocationId?: string;
};

const BonusPayment: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  mode = "create",
  modalData,
  selectedMonth,
  userId,
  onUpdate,
  userWorkLocations = [],
  defaultLocationId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const handleCancel = () => {
    onCloseModal();
    form.resetFields();
  };

  const onFinish = async (values: any) => {
    if (mode === "create") {
      await axios.post(`${BASE_API_URL}/userPayments/`, {
        userId: userId,
        bonus: {
          date: values.date,
          total: values.total,
          description: values.description,
          locationId: values.locationId || null,
        },
      });
    } else {
      if (!modalData) return;
      await axios.put(
        `${BASE_API_URL}/userPayments/${modalData._id}/${modalData.userId._id}`,
        {
          bonus: {
            date: values.date,
            total: values.total,
            description: values.description,
            locationId: values.locationId || null,
          },
        },
      );
    }

    onUpdate();
    onCloseModal();
    form.resetFields();
  };

  const totalBonus =
    modalData?.bonus?.reduce(
      (acc: number, item: any) => acc + Number(item.total || 0),
      0,
    ) || 0;

  return (
    <Modal
      title={<div>{t("Bonus Payment")}</div>}
      open={isOpen}
      onCancel={handleCancel}
      footer={[]}
      className="standardHeader"
    >
      {/* ✅ Lista bonusa */}
      <List
        dataSource={modalData?.bonus || []}
        locale={{ emptyText: t("No bonuses yet") }}
        renderItem={(item: any) => (
          <List.Item
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              marginBottom: 8,
              padding: "8px 12px",
              background: "#fafafa",
            }}
          >
            <Flex
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
            >
              <div>
                <Text strong>{format(new Date(item.date), "dd.MM.yyyy")}</Text>
                {item.description && (
                  <div style={{ fontSize: 12, color: "#777" }}>
                    {item.description}
                  </div>
                )}
              </div>

              {/* ✅ DESNO: ukupno + delete dugme */}
              <Flex align="center" gap="small">
                <Text style={{ color: "#389e0d", fontWeight: 500 }}>
                  +{item.total} RSD
                </Text>

                <Popconfirm
                  title={t("Are you sure you want to delete this bonus?")}
                  okText={t("Yes")}
                  cancelText={t("No")}
                  onConfirm={async () => {
                    await axios.delete(
                      `${BASE_API_URL}/deleteBonus/${item._id}/${modalData.userId._id}`,
                    );
                    onUpdate();
                  }}
                >
                  <Button danger size="small">
                    {t("Delete")}
                  </Button>
                </Popconfirm>
              </Flex>
            </Flex>
          </List.Item>
        )}
      />

      {/* ✅ Ukupno */}
      {modalData?.bonus?.length > 0 && (
        <Flex justify="end" style={{ marginBottom: 16 }}>
          <Text strong>
            {t("Total")}:{" "}
            <span style={{ color: "#389e0d" }}>
              {totalBonus.toLocaleString()} RSD
            </span>
          </Text>
        </Flex>
      )}

      {/* ✅ Forma */}
      <Form
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          name="date"
          label={t("Date")}
          rules={[{ required: true, message: t("Please select date!") }]}
        >
          <DatePicker placeholder={t("Select date")} />
        </Form.Item>

        {userWorkLocations.length > 0 && (
          <Form.Item
            name="locationId"
            label={t("Work Location")}
            initialValue={defaultLocationId}
            rules={[
              {
                required: true,
                message: t("Please select work location!"),
              },
            ]}
          >
            <Select placeholder={t("Select work location")}>
              {userWorkLocations.map((location: any) => (
                <Select.Option key={location._id} value={location._id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="total"
          label={t("Bonus Amount")}
          rules={[{ required: true, message: t("Please enter bonus amount!") }]}
        >
          <Input type="number" placeholder={t("Enter amount")} />
        </Form.Item>

        <Form.Item label={t("Description")} name="description">
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            {t("Save")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BonusPayment;
